import { noop } from 'lodash';

interface TypeCallQueue {
  execute: () => void;
  cancel: () => void;
}

export function concurrencyLimiter(limit) {
  let activeCalls = 0;
  const callQueue: TypeCallQueue[] = [];

  const decrementActiveCall = () => {
    // in case there is a cancelled call, to avoid negative count
    activeCalls = Math.max(activeCalls - 1, 0);
  };

  // trigger next call
  const next = () => {
    decrementActiveCall();
    dispatch();
  };

  // add function to queue
  const addToQueue = (fn, params, resolve, reject) => {
    let cancelled = false;

    const execute = () => {
      if (cancelled) {
        next();
        return;
      }
      // dispatch next in queue on success or on error
      fn(...params)
        .then((res) => {
          resolve(res);
          next();
        })
        .catch((err) => {
          reject(err);
          next();
        });
    };

    const cancel = () => {
      cancelled = true;
      reject('cancelled');
    };

    callQueue.push({ execute, cancel });

    return cancel;
  };

  // if within limit trigger next from queue
  const dispatch = () => {
    if (activeCalls < limit) {
      const entry = callQueue.shift();
      if (entry) {
        activeCalls++;
        entry.execute();
      }
    }
  };

  // reject pending calls
  const clearQueue = () => {
    while (callQueue.length > 0) {
      const entry = callQueue.pop();
      if (entry) {
        entry.cancel();
      }
    }
  };

  // adds function call to queue
  // calls dispatch to process queue
  const returnFn = (fn, ...params) => {
    let cancel = noop;
    const response = new Promise((resolve, reject) => {
      cancel = addToQueue(fn, params, resolve, reject);
    });
    dispatch();
    return { response, cancel };
  };
  returnFn.clearQueue = clearQueue;

  return returnFn;
}
